import { useTranslation } from 'react-i18next';
import { Form } from '@amzn/storm-ui-column-form-kit';
import { minus, plus } from '@amzn/storm-ui-icons';

import { CustomIconButton } from '../../../../UiComponentsLibrary/CustomIconButton';
import { CustomIconButtonColor } from '../../../../UiComponentsLibrary/CustomIconButton/constants/CustomIconButtonColor';
import { CustomConfirmationModal } from '../../../../UiComponentsLibrary/CustomConfirmationModal';
import { Accordion } from '../../../../Shared/components/Accordion';
import { TAG_TYPES } from '../../../../Shared/constants/commonConstants';
import { DetailedFormType } from '../constants/DetailedBookingConstants';
import { MultiSlotDetailedFormProps } from '../interface/DetailedBookingInterface';
import CreateDetailedBookingIMPForm from './CreateDetailedBookingIMPForm';
import CreateDetailedBookingSOVForm from './CreateDetailedBookingSOVForm';
import {
  ContextualNodeHeader,
  ContextualNodeTitle,
} from './CreateBookingForm/CreateDetailedBookingForm.styles';
import { FormFieldWrapper } from './FormFieldWrapper';
import { SlotWrapper } from './MultiSlotDetailedForm.styles';
import { useMultiSlotDetailedForm } from '../hooks/useMultiSlotDetailedForm';

const MultiSlotDetailedForm = (props: MultiSlotDetailedFormProps) => {
  const {
    initialData,
    entryId,
    isEditMode,
    bookingId,
    detailedFormType,
    resetDetailedFormErrors,
    setResetDetailedFormErrors,
  } = props;

  const { t } = useTranslation();
  const {
    commonFields,
    slotProps: {
      slotEntries,
      slotIdToExpandedAccordionMap,
      getSlotDisplayName,
      slotIdToSlotNameMap,
      toggleAccordion,
      handleSlotSelectionChange,
      getPreSelectedSlotNames,
    },
    formProps: {
      handleSlotFormSubmit,
      slotIdToValidationTriggerMap,
      getExistingNodesForSlot,
      getAvailableSlotPackages,
      handleSlotBudgetChange,
      getBudgetUsedByOtherSlots,
    },
    confirmationModalProps: {
      isBudgetLimitModalOpen,
      handleBudgetLimitModalClose,
      roValue,
    },
    iconButtonProps: { handleAddSlot, handleRemoveSlot, isAddSlotDisabled },
  } = useMultiSlotDetailedForm(props);

  return (
    <>
      <SlotWrapper>
        <Form id="common-booking-fields">
          {commonFields.map((field) => (
            <FormFieldWrapper
              key={field.id}
              id={field.id}
              type={TAG_TYPES.INPUT_TAG_TYPE}
              label={field.label}
              value={field.value}
              placeholder=""
              disabled
            />
          ))}
        </Form>

        <ContextualNodeHeader>
          <ContextualNodeTitle>{t('slot-details-heading')}</ContextualNodeTitle>
          <CustomIconButton
            icon={plus}
            label={t('add-more-slots-button')}
            backgroundColor={CustomIconButtonColor.search}
            handleOnClickCb={handleAddSlot}
            disabled={isAddSlotDisabled}
          />
        </ContextualNodeHeader>

        {slotEntries.map((entry) => {
          const isExpanded = slotIdToExpandedAccordionMap[entry.id] ?? false;
          const slotDisplayName = getSlotDisplayName(entry.id);
          const hasSelection = !!slotIdToSlotNameMap[entry.id];

          return (
            <Accordion
              key={entry.id}
              id={`slot-accordion-${entry.id}`}
              title={t('slot-label')}
              subtitle={hasSelection ? slotDisplayName : undefined}
              isExpanded={isExpanded}
              onToggle={() => toggleAccordion(entry.id)}
              headerActions={
                <CustomIconButton
                  icon={minus}
                  label={t('remove-slot-button')}
                  backgroundColor={CustomIconButtonColor.delete}
                  handleOnClickCb={() => handleRemoveSlot(entry.id)}
                  disabled={slotEntries.length <= 1}
                />
              }
            >
              {detailedFormType === DetailedFormType.IMP ? (
                <CreateDetailedBookingIMPForm
                  key={`imp-${entry.id}`}
                  initialData={initialData}
                  bookingId={bookingId}
                  entryId={entryId || 0}
                  onFormSubmit={(formData, contextualNodes) =>
                    handleSlotFormSubmit(entry.id, formData, contextualNodes)
                  }
                  validateFormTrigger={
                    slotIdToValidationTriggerMap[entry.id] ?? false
                  }
                  existingContextualNodes={getExistingNodesForSlot(entry.id)}
                  resetDetailedFormErrors={resetDetailedFormErrors}
                  setResetDetailedFormErrors={setResetDetailedFormErrors}
                  filteredSlotPackages={getAvailableSlotPackages(entry.id)}
                  isEditMode={isEditMode}
                  onSlotSelectionChange={(slotName) =>
                    handleSlotSelectionChange(entry.id, slotName)
                  }
                  preSelectedSlotNames={getPreSelectedSlotNames(entry.id)}
                  budgetUsedByOtherSlots={getBudgetUsedByOtherSlots(entry.id)}
                  onSlotBudgetChange={(totalBudget) =>
                    handleSlotBudgetChange(entry.id, totalBudget)
                  }
                  hideCommonFields
                />
              ) : (
                <CreateDetailedBookingSOVForm
                  key={`sov-${entry.id}`}
                  initialData={initialData}
                  bookingId={bookingId}
                  entryId={entryId || 0}
                  onFormSubmit={(formData, contextualNodes) =>
                    handleSlotFormSubmit(entry.id, formData, contextualNodes)
                  }
                  validateFormTrigger={
                    slotIdToValidationTriggerMap[entry.id] ?? false
                  }
                  existingContextualNodes={getExistingNodesForSlot(entry.id)}
                  resetDetailedFormErrors={resetDetailedFormErrors}
                  setResetDetailedFormErrors={setResetDetailedFormErrors}
                  filteredSlotPackages={getAvailableSlotPackages(entry.id)}
                  isEditMode={isEditMode}
                  onSlotSelectionChange={(slotName) =>
                    handleSlotSelectionChange(entry.id, slotName)
                  }
                  preSelectedSlotNames={getPreSelectedSlotNames(entry.id)}
                  budgetUsedByOtherSlots={getBudgetUsedByOtherSlots(entry.id)}
                  onSlotBudgetChange={(totalBudget) =>
                    handleSlotBudgetChange(entry.id, totalBudget)
                  }
                  hideCommonFields
                />
              )}
            </Accordion>
          );
        })}
      </SlotWrapper>
      <CustomConfirmationModal
        isOpen={isBudgetLimitModalOpen}
        onConfirmCb={handleBudgetLimitModalClose}
        onCancelCb={handleBudgetLimitModalClose}
        title={t('budget-limit-exceeded-title')}
        message={t('budget-limit-exceeded-message', {
          roValue: roValue.toLocaleString(),
        })}
        confirmButtonText={t('ok-button')}
      />
    </>
  );
};

export default MultiSlotDetailedForm;
